/**
 * Turns a signed distance field into a triangle mesh (surface nets). The field is sampled on a grid only in blocks
 * the surface can pass through; each grid cell the surface crosses gets one vertex, which is then pulled onto the
 * surface, and its normal is the field's gradient, so shading stays smooth even where the mesh is coarse.
 */
export type Field = (x: number, y: number, z: number) => number;
export interface FieldMeshOptions {
  min: readonly [number, number, number]; max: readonly [number, number, number];
  /** Grid spacing. */
  cell: number;
  /** How steeply the field can change; blocks farther from the surface than this allows are skipped. */
  lipschitz?: number;
  /** Triangles whose vertices all fail this test are dropped, such as the hidden inner side of a shell. */
  keep?: (x: number, y: number, z: number) => boolean;
  /** Newton steps pulling each vertex onto the surface (default 1). */
  steps?: number;
}
export interface FieldMesh { positions: Float32Array; normals: Float32Array; indices: Uint32Array }

// Cube corners as bits (x = 1, y = 2, z = 4) and the twelve edges between them.
const EDGES = [0, 1, 2, 3, 4, 5, 6, 7, 0, 2, 1, 3, 4, 6, 5, 7, 0, 4, 1, 5, 2, 6, 3, 7];

export function meshField(field: Field, options: FieldMeshOptions): FieldMesh {
  const h = options.cell, [x0, y0, z0] = options.min, lipschitz = options.lipschitz ?? 1.6;
  const nx = Math.max(1, Math.ceil((options.max[0] - x0) / h)), ny = Math.max(1, Math.ceil((options.max[1] - y0) / h)), nz = Math.max(1, Math.ceil((options.max[2] - z0) / h));
  const sx = nx + 1, sxy = sx * (ny + 1), values = new Float32Array(sxy * (nz + 1)).fill(NaN);
  const sample = (i: number, j: number, k: number) => {
    const n = i + j * sx + k * sxy;
    let v = values[n];
    if (v !== v) { v = field(x0 + i * h, y0 + j * h, z0 + k * h); values[n] = v; }
    return v;
  };
  const vertexOf = new Int32Array(nx * ny * nz).fill(-1), cells: number[] = [], points: number[] = [];
  const corner = new Float64Array(8), block = 5;
  for (let bk = 0; bk < nz; bk += block) for (let bj = 0; bj < ny; bj += block) for (let bi = 0; bi < nx; bi += block) {
    const ei = Math.min(nx, bi + block), ej = Math.min(ny, bj + block), ek = Math.min(nz, bk + block);
    const half = .5 * h * Math.sqrt((ei - bi) ** 2 + (ej - bj) ** 2 + (ek - bk) ** 2);
    if (Math.abs(field(x0 + (bi + ei) * .5 * h, y0 + (bj + ej) * .5 * h, z0 + (bk + ek) * .5 * h)) > lipschitz * half + h) continue;
    for (let k = bk; k < ek; k++) for (let j = bj; j < ej; j++) for (let i = bi; i < ei; i++) {
      let inside = 0;
      for (let c = 0; c < 8; c++) { const v = sample(i + (c & 1), j + (c >> 1 & 1), k + (c >> 2)); corner[c] = v; if (v < 0) inside |= 1 << c; }
      if (inside === 0 || inside === 255) continue;
      // The vertex sits at the mean of the points where the surface crosses the cell's edges.
      let ax = 0, ay = 0, az = 0, crossings = 0;
      for (let e = 0; e < 24; e += 2) {
        const a = EDGES[e], b = EDGES[e + 1], va = corner[a], vb = corner[b];
        if ((va < 0) === (vb < 0)) continue;
        const t = va / (va - vb);
        ax += (a & 1) + t * ((b & 1) - (a & 1)); ay += (a >> 1 & 1) + t * ((b >> 1 & 1) - (a >> 1 & 1)); az += (a >> 2) + t * ((b >> 2) - (a >> 2));
        crossings++;
      }
      const cell = i + j * nx + k * nx * ny;
      vertexOf[cell] = points.length / 3; cells.push(cell);
      points.push(x0 + (i + ax / crossings) * h, y0 + (j + ay / crossings) * h, z0 + (k + az / crossings) * h);
    }
  }
  // Each grid edge the surface crosses joins the four cells around it in a quad, facing out of the field.
  const triangles: number[] = [];
  const quad = (a: number, b: number, c: number, d: number, outward: number, axis: number) => {
    if (a < 0 || b < 0 || c < 0 || d < 0) return;
    // Wind the quad so its normal points from inside to outside along the edge's axis.
    const e1 = [points[c * 3] - points[a * 3], points[c * 3 + 1] - points[a * 3 + 1], points[c * 3 + 2] - points[a * 3 + 2]];
    const e2 = [points[d * 3] - points[b * 3], points[d * 3 + 1] - points[b * 3 + 1], points[d * 3 + 2] - points[b * 3 + 2]];
    const n = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
    if (n[axis] * outward < 0) { const swap = b; b = d; d = swap; }
    // Split along the shorter diagonal.
    const diagonal = (p: number, q: number) => (points[p * 3] - points[q * 3]) ** 2 + (points[p * 3 + 1] - points[q * 3 + 1]) ** 2 + (points[p * 3 + 2] - points[q * 3 + 2]) ** 2;
    if (diagonal(a, c) <= diagonal(b, d)) triangles.push(a, b, c, a, c, d);
    else triangles.push(a, b, d, b, c, d);
  };
  for (const cell of cells) {
    const i = cell % nx, j = Math.floor(cell / nx) % ny, k = Math.floor(cell / (nx * ny)), v0 = sample(i, j, k);
    const at = (di: number, dj: number, dk: number) => vertexOf[(i + di) + (j + dj) * nx + (k + dk) * nx * ny];
    const self = vertexOf[cell];
    // Edges from the cell's lowest corner along x, y and z; the cell owns them when it is last in the other two axes.
    if (j > 0 && k > 0 && (v0 < 0) !== (sample(i + 1, j, k) < 0)) quad(self, at(0, -1, 0), at(0, -1, -1), at(0, 0, -1), v0 < 0 ? 1 : -1, 0);
    if (i > 0 && k > 0 && (v0 < 0) !== (sample(i, j + 1, k) < 0)) quad(self, at(-1, 0, 0), at(-1, 0, -1), at(0, 0, -1), v0 < 0 ? 1 : -1, 1);
    if (i > 0 && j > 0 && (v0 < 0) !== (sample(i, j, k + 1) < 0)) quad(self, at(-1, 0, 0), at(-1, -1, 0), at(0, -1, 0), v0 < 0 ? 1 : -1, 2);
  }
  const count = points.length / 3, normals = settle(field, points, h, options.steps ?? 1);
  faceAlong(points, normals, triangles);
  // Drop hidden triangles, then the vertices nothing uses.
  let kept = triangles;
  if (options.keep) {
    const keep = options.keep, visible = new Uint8Array(count);
    for (let n = 0; n < count; n++) visible[n] = keep(points[n * 3], points[n * 3 + 1], points[n * 3 + 2]) ? 1 : 0;
    kept = [];
    for (let t = 0; t < triangles.length; t += 3) if (visible[triangles[t]] || visible[triangles[t + 1]] || visible[triangles[t + 2]]) kept.push(triangles[t], triangles[t + 1], triangles[t + 2]);
  }
  const remap = new Int32Array(count).fill(-1), positions: number[] = [], outNormals: number[] = [], indices = new Uint32Array(kept.length);
  kept.forEach((vertex, index) => {
    if (remap[vertex] < 0) {
      remap[vertex] = positions.length / 3;
      positions.push(points[vertex * 3], points[vertex * 3 + 1], points[vertex * 3 + 2]);
      outNormals.push(normals[vertex * 3], normals[vertex * 3 + 1], normals[vertex * 3 + 2]);
    }
    indices[index] = remap[vertex];
  });
  return { positions: new Float32Array(positions), normals: new Float32Array(outNormals), indices };
}

/**
 * Winds each triangle to face along its vertices' normals. Pulling vertices onto the surface can fold a sliver of a
 * triangle over; turned round, it shades like its neighbours instead of showing its back.
 */
export function faceAlong(points: { [index: number]: number }, normals: { [index: number]: number }, indices: { length: number; [index: number]: number }) {
  for (let t = 0; t < indices.length; t += 3) {
    const a = indices[t] * 3, b = indices[t + 1] * 3, c = indices[t + 2] * 3;
    const ux = points[b] - points[a], uy = points[b + 1] - points[a + 1], uz = points[b + 2] - points[a + 2];
    const vx = points[c] - points[a], vy = points[c + 1] - points[a + 1], vz = points[c + 2] - points[a + 2];
    const along = (uy * vz - uz * vy) * (normals[a] + normals[b] + normals[c]) + (uz * vx - ux * vz) * (normals[a + 1] + normals[b + 1] + normals[c + 1])
      + (ux * vy - uy * vx) * (normals[a + 2] + normals[b + 2] + normals[c + 2]);
    if (along < 0) { const swap = indices[t + 1]; indices[t + 1] = indices[t + 2]; indices[t + 2] = swap; }
  }
}

/**
 * Pulls points onto the field's surface along its gradient (`steps` Newton steps, each moving at most `cell`) and
 * returns the gradient at each as its normal. Updates `points` in place.
 */
export function settle(field: Field, points: { length: number; [index: number]: number }, cell: number, steps = 1): Float32Array {
  const count = points.length / 3, normals = new Float32Array(count * 3), e = cell * .3;
  for (let n = 0; n < count; n++) {
    let x = points[n * 3], y = points[n * 3 + 1], z = points[n * 3 + 2], gx = 0, gy = 0, gz = 0;
    for (let iteration = 0; iteration <= steps; iteration++) {
      // Tetrahedral differences: four samples give the value and the gradient.
      const f0 = field(x + e, y - e, z - e), f1 = field(x - e, y - e, z + e), f2 = field(x - e, y + e, z - e), f3 = field(x + e, y + e, z + e);
      gx = (f0 - f1 - f2 + f3) / (4 * e); gy = (-f0 - f1 + f2 + f3) / (4 * e); gz = (-f0 + f1 - f2 + f3) / (4 * e);
      const g2 = gx * gx + gy * gy + gz * gz, value = (f0 + f1 + f2 + f3) / 4;
      if (iteration === steps || g2 < 1e-12) break;
      let step = value / g2;
      const length = Math.abs(step) * Math.sqrt(g2);
      if (length > cell) step *= cell / length;
      x -= gx * step; y -= gy * step; z -= gz * step;
    }
    points[n * 3] = x; points[n * 3 + 1] = y; points[n * 3 + 2] = z;
    const g = Math.sqrt(gx * gx + gy * gy + gz * gz) || 1;
    normals[n * 3] = gx / g; normals[n * 3 + 1] = gy / g; normals[n * 3 + 2] = gz / g;
  }
  return normals;
}
